/**
 * Attachment configuration for the automation "Send Email" step.
 *
 * Stored inside `AutomationStepEntity.config.attachments` (JSON). Two kinds:
 *
 * - `file`: a fixed file the flow author uploaded in the step editor
 *   (`POST /files/upload`, then a presigned PUT). Only the file id is
 *   stored; the step resolves it to bytes at send time. Same content for
 *   every recipient — price sheets, brochures, terms.
 * - `entity_document`: a PDF rendered at send time for the entity the flow
 *   is running against (quote, order or invoice). `entityId` goes through
 *   the automation variable resolver before the step runs, so it is usually
 *   a `{{...}}` reference rather than a literal id.
 */

export const AUTOMATION_EMAIL_ENTITY_DOCUMENT_TYPES = [
	'quote_pdf',
	'order_pdf',
	'invoice_pdf',
] as const;

export type AutomationEmailEntityDocumentType =
	(typeof AUTOMATION_EMAIL_ENTITY_DOCUMENT_TYPES)[number];

export interface AutomationEmailFileAttachmentModel {
	kind: 'file';
	/** Numeric id of the uploaded `file` row. */
	fileId: number;
	/** Display name captured at upload so the editor can list it without a lookup. */
	name: string;
	/** Byte size captured at upload, used for the client-side limit check. */
	size: number;
}

export interface AutomationEmailEntityDocumentAttachmentModel {
	kind: 'entity_document';
	documentType: AutomationEmailEntityDocumentType;
	/**
	 * Entity id, or a `{{...}}` variable reference resolved before execution.
	 * Kept as a string because the variable resolver operates on strings.
	 */
	entityId: string;
}

export type AutomationEmailAttachmentModel =
	| AutomationEmailFileAttachmentModel
	| AutomationEmailEntityDocumentAttachmentModel;
